import React, { useState } from 'react';
import { useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Stack,
    Divider,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    useTheme
} from '@mui/material';

import {
    AccountBalanceWallet,
    Message,
    History,
    Add
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import api from '../../api/axios';

// --- Interfaces ---
interface WalletSummary {
    sms: { balance: number; updated_at: string };
    whatsapp: { balance: number; updated_at: string };
    sms_credit_rate: number;
}

const Wallet = () => {
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [topUpOpen, setTopUpOpen] = useState(false);
    const [creditsToBuy, setCreditsToBuy] = useState<number>(100);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const queryClient = useQueryClient();
    const theme = useTheme();

    const openTopUp = () => {
        setCreditsToBuy(100);
        setTopUpOpen(true);
        setError('');
    };

    const processTopUp = async () => {
        if (!summary?.sms_credit_rate || summary.sms_credit_rate <= 0) {
            setError('SMS credit rate is not configured. Contact admin.');
            return;
        }
        setIsProcessing(true);
        setError('');
        try {
            const amount = creditsToBuy * summary.sms_credit_rate;
            const res = await api.post('/tenant/payments/sms/create-order', {
                credits: creditsToBuy,
                amount: amount,
            });

            const orderData = res.data?.data;
            if (!orderData?.razorpay_order_id) throw new Error('Failed to create order');

            const options = {
                key: orderData.razorpay_key,
                amount: orderData.amount,
                currency: orderData.currency,
                name: 'Leader360',
                description: 'SMS Credits Topup',
                order_id: orderData.razorpay_order_id,
                handler: async (response: any) => {
                    try {
                        const verifyRes = await api.post('/tenant/payments/sms/verify', {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            credits: creditsToBuy,
                        });

                        if (verifyRes.data?.status === 'success') {
                            queryClient.invalidateQueries({ queryKey: ['wallet-summary'] });
                            queryClient.invalidateQueries({ queryKey: ['sms-history'] });
                            setTopUpOpen(false);
                            setSuccessMsg(`${creditsToBuy} SMS credits added successfully!`);
                        } else {
                            setError('Payment verification failed');
                        }
                    } catch (err: any) {
                        setError(err.response?.data?.message || 'Payment verification failed');
                    }
                    setIsProcessing(false);
                },
                modal: { ondismiss: () => setIsProcessing(false) },
                theme: { color: theme.palette.primary.main },
            };

            const razorpay = new (window as any).Razorpay(options);
            razorpay.open();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to initiate payment');
            setIsProcessing(false);
        }
    };

    // --- Queries ---
    const { data: summary, isLoading: summaryLoading } = useQuery<WalletSummary>({
        queryKey: ['wallet-summary'],
        queryFn: async () => (await api.get('/tenant/wallet/summary')).data.data
    });

    const { data: smsHistoryData, isLoading: smsHistoryLoading } = useQuery({
        queryKey: ['sms-history', pageModel.page, pageModel.pageSize],
        queryFn: async () => {
            const params = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            const res = await api.get('/tenant/wallet/sms/history', { params });
            return res.data?.data;
        },
        placeholderData: keepPreviousData,
    });

    // --- Columns ---
    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr.',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        {
            field: 'amount',
            headerName: 'Credits',
            width: 120,
            renderCell: (params) => (
                <Typography
                    color={params.row.type === 'credit' ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                >
                    {params.row.type === 'credit' ? '+' : '-'}{params.value}
                </Typography>
            )
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value.toUpperCase()}
                    color={params.row.type === 'credit' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                />
            )
        },
        { field: 'balance_after', headerName: 'Balance After', width: 130 },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
        {
            field: 'created_at',
            headerName: 'Date',
            width: 180,
            valueFormatter: (value) => value ? new Date(value).toLocaleString() : 'N/A'
        },
    ];

    const creditRate = summary?.sms_credit_rate || 0;
    const totalCost = creditsToBuy * creditRate;

    if (summaryLoading) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AccountBalanceWallet color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4">Wallet & Credits</Typography>
            </Box>

            {/* SMS Wallet Card */}
            <Card sx={{ bgcolor: '#e3f2fd', mb: 4 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                SMS Balance
                            </Typography>
                            <Typography variant="h3" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: '2rem', sm: '3rem' } }}>
                                {summary?.sms?.balance || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Credits Available
                            </Typography>
                        </Box>
                        <Message color="primary" fontSize="large" />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        fullWidth
                        onClick={openTopUp}
                    >
                        Buy SMS Credits
                    </Button>
                </CardContent>
            </Card>

            {/* SMS History */}
            <Paper sx={{ width: '100%' }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <History color="action" />
                    <Typography variant="h6">SMS Transaction History</Typography>
                </Box>
                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={smsHistoryData?.data || []}
                        columns={columns}
                        loading={smsHistoryLoading}
                        paginationModel={pageModel}
                        onPaginationModelChange={setPageModel}
                        pageSizeOptions={[10, 25, 50]}
                        paginationMode="server"
                        rowCount={smsHistoryData?.total || smsHistoryData?.last_page * pageModel.pageSize || 0}
                        disableRowSelectionOnClick
                    />
                </Box>
            </Paper>

            {/* Buy SMS Credits Dialog */}
            <Dialog open={topUpOpen} onClose={() => !isProcessing && setTopUpOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Buy SMS Credits</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Number of Credits"
                            type="number"
                            fullWidth
                            value={creditsToBuy}
                            onChange={(e) => setCreditsToBuy(Math.max(1, parseInt(e.target.value) || 0))}
                            helperText={`1 Credit = ₹${creditRate.toFixed(2)}`}
                            disabled={isProcessing}
                            slotProps={{ htmlInput: { min: 1 } }}
                        />

                        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography color="text.secondary">Rate per credit:</Typography>
                                <Typography fontWeight="bold">₹{creditRate.toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography color="text.secondary">Credits:</Typography>
                                <Typography fontWeight="bold">{creditsToBuy}</Typography>
                            </Stack>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography fontWeight="bold">Total Cost:</Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">₹{totalCost.toFixed(2)}</Typography>
                            </Stack>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTopUpOpen(false)} disabled={isProcessing}>Cancel</Button>
                    <Button
                        onClick={processTopUp}
                        variant="contained"
                        disabled={isProcessing || creditsToBuy < 1 || creditRate <= 0}
                    >
                        {isProcessing ? 'Processing...' : `Pay ₹${totalCost.toFixed(2)}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Alert
                severity="success"
                sx={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, display: successMsg ? 'flex' : 'none', boxShadow: 3 }}
                onClose={() => setSuccessMsg('')}
            >
                {successMsg}
            </Alert>
        </Box>
    );
};

export default Wallet;
